import {Component, OnDestroy, OnInit} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {UserRegistrationService} from "../../service-auth/user-registration.service";
import {UserLoginService} from "../../service-auth/user-login.service";
import {LoggedInCallback} from "../../service-auth/cognito.service";
import {StartupService} from "../../service-ui/startup.service";
import {environment as env} from "../../../environments/environment";
import {LanguagePipe} from "../../pipe/language.pipe";

@Component({
    selector: 'app-account',
    templateUrl: './confirmRegistration.html',
    styleUrls: ['../component-account.scss']

})

export class RegistrationConfirmationComponent implements OnInit, OnDestroy {
    environment = env;
    confirmationCode: string;
    email: string;
    errorMessage: string;

    /**
     * True when the failure means "the account exists and is confirmed, but
     * the server-side setup behind it did not finish".
     *
     * This is the state a migrating legacy user lands in when the
     * PostConfirmation Lambda throws, and it is a dead end unless we say so:
     * confirming again returns NotAuthorizedException ("Current status is
     * CONFIRMED"), registering again returns UsernameExistsException, and the
     * only route forward -- signing in -- is the one the screen gives no
     * reason to try.
     */
    accountExistsNeedsSignIn = false;

    private sub: any;

    constructor(public regService: UserRegistrationService, public router: Router, public route: ActivatedRoute) {
    }

    ngOnInit() {
        this.sub = this.route.params.subscribe(params => {
            this.email = params['username'];

        });

        this.errorMessage = null;
    }

    ngOnDestroy() {
        this.sub.unsubscribe();
    }

    onConfirmRegistration() {
        this.errorMessage = null;
        this.accountExistsNeedsSignIn = false;
        this.regService.confirmRegistration(this.email, this.confirmationCode, this);
    }

    cognitoCallback(message: string, result: any, code?: string) {
        if (message != null) { // error
            // Always logged with the code -- the raw text is the only thing
            // that identifies which of the Lambda exceptions occurred, and it
            // is no longer being shown verbatim to the user.
            console.error('confirmRegistration failed', { code: code, message: message });
            if (this.isAccountCreatedButSetupIncomplete(message, code)) {
                this.accountExistsNeedsSignIn = true;
                // Sign-in first, contact-us second, and in that order: the
                // repair path runs on login, so a second sign-in resolves most
                // of these without anyone being contacted. The message says
                // "already created an account" explicitly so support does not
                // open by telling the user to register -- which would fail with
                // UsernameExistsException and waste both sides' time.
                this.errorMessage = LanguagePipe.get(
                    'Your account was created, but the setup behind it did not finish. Please sign in - signing in again will often complete it. If it does not, contact us and let us know you have already created an account, and a system administrator will fix it.',
                    'compressSpaces');
            } else {
                this.accountExistsNeedsSignIn = false;
                this.errorMessage = message;
            }
        } else { // success
            // move to the next step
            console.log("Moving to secure");
            // this.configs.curUser = result.user;
            this.router.navigate(['/secure']);
        }
    }

    /**
     * Does this failure mean the account now exists, rather than that the user
     * mistyped the code?
     *
     * Cognito confirms the user and only THEN invokes the PostConfirmation
     * trigger, so every one of these codes arrives on an account that is
     * already CONFIRMED. Telling such a user to check their confirmation code
     * sends them round a loop that cannot terminate.
     *
     * Matched on the documented AWS error codes rather than on message text,
     * which is not part of any contract. The NotAuthorizedException case is
     * additionally checked against the message because that code is generic --
     * only the "Current status is CONFIRMED" variant means what we want here.
     */
    private isAccountCreatedButSetupIncomplete(message: string, code?: string): boolean {
        const errorCode = (code || '').trim();
        if (errorCode === 'UnexpectedLambdaException'
            || errorCode === 'UserLambdaValidationException'
            || errorCode === 'InvalidLambdaResponseException') {
            return true;
        }
        if (errorCode === 'NotAuthorizedException' && /CONFIRMED/i.test(message || '')) {
            return true;
        }
        return false;
    }

    goToLogin() {
        this.router.navigate(['/home/login']);
    }
}





